import React from 'react';

import { useIntl } from 'react-intl';

import { PickerSelection } from '@rbi-ctg/menu';
import { ComboSlotType } from 'components/product-detail-v2/product-customization/ComboSlotType';
import { getComboSlotType } from 'components/product-detail-v2/product-customization/utils';
import { IUserSelection } from 'state/product-wizard-v2/types';
import { isCombo } from 'utils/menu';

import { findNextValidComboSlotKey, getNextComboSlotKey } from './utils';

export const useGetNextComboSlotLabel = ({
  slotKeyToCustomize,
  userSelections,
  selectedProduct,
}: {
  slotKeyToCustomize: string;
  userSelections: IUserSelection;
  selectedProduct: PickerSelection;
}) => {
  const { formatMessage } = useIntl();

  const nextComboSlotKey = React.useMemo(
    () =>
      getNextComboSlotKey({
        slotKeyToCustomize,
        userSelections,
      }),
    [slotKeyToCustomize, userSelections]
  );

  if (!userSelections.comboSlot || !slotKeyToCustomize) {
    return formatMessage({ id: 'nextCustomize' });
  }

  if (!nextComboSlotKey) {
    return formatMessage({
      id: isCombo(selectedProduct) ? 'nextReviewYourMeal' : 'nextReviewYourSelection',
    });
  }

  const nextValidComboSlotKey = findNextValidComboSlotKey(nextComboSlotKey, userSelections);

  const nextItemToCustomize = nextValidComboSlotKey
    ? userSelections.comboSlot.get(nextValidComboSlotKey)?.selectedItem
    : null;

  if (!nextItemToCustomize) {
    return formatMessage({ id: 'nextCustomize' });
  }

  // Get the combo slot type for the next item
  const comboSlotType = getComboSlotType(nextItemToCustomize);

  switch (comboSlotType) {
    case ComboSlotType.SIDES:
      return formatMessage({ id: 'nextSelectYourSide' });
    case ComboSlotType.DRINKS:
      return formatMessage({ id: 'nextSelectYourDrink' });
    default:
      return formatMessage({ id: 'nextCustomize' });
  }
};
